import type { Identifier } from "ra-core";

import type {
  Contact,
  SequenceDef,
  SlateForm,
  WorkflowDef,
} from "../types";

/**
 * Deterministic sample activity for marketing assets - form fills, sequence
 * enrollments, workflow runs. Same pattern as the campaign send simulator:
 * everything derives from a stable hash of (asset id, contact id) so the
 * numbers hold steady across reloads. At deployment these tables read the
 * real event spine (form.submitted, sequence.enrolled, workflow.executed)
 * instead of simulating.
 *
 * DEMO ONLY: on a live deployment every simulator returns empty - fabricated
 * activity attached to real contacts is worse than an honest zero. The
 * starter assets stay; their counts start at 0 until the engines run.
 */

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

/** Stable 32-bit hash of any string - the seed for all sample activity. */
export const activitySeed = (assetId: string, contactId: Identifier): number => {
  const s = `${assetId}:${String(contactId)}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const dayMs = 86400_000;
/** A deterministic timestamp N days back, pinned to a stable hour. */
const pastIso = (daysBack: number, seed: number) => {
  const d = new Date(Date.now() - daysBack * dayMs);
  d.setHours(8 + (seed % 10), (seed * 7) % 60, 0, 0);
  return d.toISOString();
};

// ---------------------------------------------------------------- forms ----

export interface FormSubmission {
  contactId: Identifier;
  name: string;
  email: string;
  at: string;
  /** Landing page the fill came from. */
  pageUrl: string;
  /** First-touch channel read from the tracking cookie. */
  channel: string;
  /** Nth fill by this contact on this form (1 = first). */
  nth: number;
}

export interface FormActivity {
  submissions: FormSubmission[];
  uniqueContacts: number;
  last30: number;
  lastAt?: string;
  /** Contacts with more than one fill, most fills first. */
  repeatFillers: { contactId: Identifier; name: string; fills: number }[];
}

const PAGES = ["/contact", "/pricing", "/resources/guide", "/", "/services"];
const CHANNELS = ["organic", "paid", "direct", "referral", "email"];

/**
 * Sample fills for a form: roughly one contact in nine submits, a handful
 * submit twice. Deterministic per (form, contact).
 */
export const simulateFormSubmissions = (
  form: SlateForm,
  contacts: Contact[],
): FormActivity => {
  if (!IS_DEMO)
    return { submissions: [], uniqueContacts: 0, last30: 0, repeatFillers: [] };
  const submissions: FormSubmission[] = [];
  for (const c of contacts) {
    const seed = activitySeed(form.id, c.id);
    if (seed % 9 !== 0) continue;
    const fills = seed % 27 === 0 ? 2 : 1;
    const firstDaysBack = 3 + (seed % 85);
    for (let n = 1; n <= fills; n++) {
      submissions.push({
        contactId: c.id,
        name: `${c.first_name} ${c.last_name}`,
        email: c.email_jsonb?.[0]?.email ?? "",
        at: pastIso(n === 1 ? firstDaysBack : Math.max(1, firstDaysBack - 20), seed + n),
        pageUrl: PAGES[seed % PAGES.length]!,
        channel: CHANNELS[(seed >> 3) % CHANNELS.length]!,
        nth: n,
      });
    }
  }
  submissions.sort((a, b) => b.at.localeCompare(a.at));

  const byContact = new Map<Identifier, { name: string; fills: number }>();
  for (const s of submissions) {
    const cur = byContact.get(s.contactId);
    byContact.set(s.contactId, { name: s.name, fills: (cur?.fills ?? 0) + 1 });
  }
  const cutoff = Date.now() - 30 * dayMs;
  return {
    submissions,
    uniqueContacts: byContact.size,
    last30: submissions.filter((s) => new Date(s.at).getTime() >= cutoff).length,
    lastAt: submissions[0]?.at,
    repeatFillers: [...byContact.entries()]
      .filter(([, v]) => v.fills > 1)
      .map(([contactId, v]) => ({ contactId, name: v.name, fills: v.fills }))
      .sort((a, b) => b.fills - a.fills),
  };
};

// ------------------------------------------------------------ sequences ----

export interface SequenceEnrollmentRow {
  contactId: Identifier;
  name: string;
  status: "active" | "completed" | "replied" | "unenrolled" | "failed";
  /** 0-based index into sequence.steps for active enrollments. */
  stepIndex: number;
  enrolledAt: string;
}

export interface SequenceActivity {
  rows: SequenceEnrollmentRow[];
  active: number;
  completed: number;
  replied: number;
}

/**
 * Sample enrollment for a sequence over its audience: about half are mid-
 * cadence, the rest completed or replied (replies only when auto-unenroll
 * is on - otherwise they count as completed).
 */
export const simulateSequenceEnrollment = (
  sequence: SequenceDef,
  members: Contact[],
): SequenceActivity => {
  if (!IS_DEMO) return { rows: [], active: 0, completed: 0, replied: 0 };
  const steps = Math.max(1, sequence.steps.length);
  const rows: SequenceEnrollmentRow[] = members.map((c) => {
    const seed = activitySeed(sequence.id, c.id);
    const bucket = seed % 10;
    const replied = sequence.unenrollOnReply && bucket >= 8;
    const status: SequenceEnrollmentRow["status"] = replied
      ? "replied"
      : bucket >= 6
        ? "completed"
        : "active";
    return {
      contactId: c.id,
      name: `${c.first_name} ${c.last_name}`,
      status,
      stepIndex: status === "active" ? seed % steps : steps - 1,
      // >> 4 decorrelates the date from the status bucket (both hash-derived)
      enrolledAt: pastIso(2 + ((seed >> 4) % 40), seed),
    };
  });
  rows.sort((a, b) => b.enrolledAt.localeCompare(a.enrolledAt));
  return {
    rows,
    active: rows.filter((r) => r.status === "active").length,
    completed: rows.filter((r) => r.status === "completed").length,
    replied: rows.filter((r) => r.status === "replied").length,
  };
};

// ------------------------------------------------------------ workflows ----

export interface WorkflowRun {
  contactId: Identifier;
  name: string;
  at: string;
}

export interface WorkflowActivity {
  runs: WorkflowRun[];
  last30: number;
  lastAt?: string;
}

/**
 * Sample runs for an enabled workflow: about one contact in eight matched
 * the trigger in the last 60 days. Disabled workflows show zero.
 */
export const simulateWorkflowRuns = (
  workflow: WorkflowDef,
  contacts: Contact[],
): WorkflowActivity => {
  if (!IS_DEMO || !workflow.enabled) return { runs: [], last30: 0 };
  const runs: WorkflowRun[] = [];
  for (const c of contacts) {
    const seed = activitySeed(workflow.id, c.id);
    if (seed % 8 !== 0) continue;
    runs.push({
      contactId: c.id,
      name: `${c.first_name} ${c.last_name}`,
      at: pastIso(1 + ((seed >> 3) % 60), seed),
    });
  }
  runs.sort((a, b) => b.at.localeCompare(a.at));
  const cutoff = Date.now() - 30 * dayMs;
  return {
    runs,
    last30: runs.filter((r) => new Date(r.at).getTime() >= cutoff).length,
    lastAt: runs[0]?.at,
  };
};

// -------------------------------------------------------------- shared ----

export const shortDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "-";
